import React, { useState } from 'react';
import { AlertTriangle, ChevronUp } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import Button from '../ui/Button';

type MergeOption = 'oldest' | 'newest';

interface DisableDuplicateEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (mergeOption: MergeOption) => void;
  isLoading?: boolean;
}

const DisableDuplicateEmailModal: React.FC<DisableDuplicateEmailModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [selectedMergeOption, setSelectedMergeOption] =
    useState<MergeOption>('oldest');
  const [isHowItWorksExpanded, setIsHowItWorksExpanded] = useState(true);

  const handleConfirm = () => {
    onConfirm(selectedMergeOption);
  };

  const handleClose = () => {
    setSelectedMergeOption('oldest'); // Reset to default when closing
    setIsHowItWorksExpanded(true);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Disable Duplicate Emails"
      size="large"
    >
      <div className="space-y-4">
        {/* Intro Message */}
        <h3 className="text-sm font-semibold text-foreground">
          If duplicate profiles are found, they need to be merged or deleted.
        </h3>

        {/* Merge Options Section */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">
            Choose how to merge profiles
          </h4>

          <div className="space-y-1.5">
            {/* Option 1: Oldest Profile */}
            <label
              className={`
                flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all
                ${
                  selectedMergeOption === 'oldest'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted/50'
                }
              `}
              style={{ outline: 'none' }}
            >
              <input
                type="radio"
                name="mergeOption"
                value="oldest"
                checked={selectedMergeOption === 'oldest'}
                onChange={(e) =>
                  setSelectedMergeOption(e.target.value as MergeOption)
                }
                disabled={isLoading}
                className="w-3.5 h-3.5 accent-primary border-border focus:ring-0 cursor-pointer"
                style={{ outline: 'none' }}
              />
              <span className="text-sm text-foreground flex-1">
                Use the oldest profile as the primary
              </span>
              <Badge variant="success" size="small" className="text-xs">
                Recommended
              </Badge>
            </label>

            {/* Option 2: Newest Profile */}
            <label
              className={`
                flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all
                ${
                  selectedMergeOption === 'newest'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted/50'
                }
              `}
              style={{ outline: 'none' }}
            >
              <input
                type="radio"
                name="mergeOption"
                value="newest"
                checked={selectedMergeOption === 'newest'}
                onChange={(e) =>
                  setSelectedMergeOption(e.target.value as MergeOption)
                }
                disabled={isLoading}
                className="w-3.5 h-3.5 accent-primary border-border focus:ring-0 cursor-pointer"
                style={{ outline: 'none' }}
              />
              <span className="text-sm text-foreground flex-1">
                Use the newest profile as the primary
              </span>
            </label>
          </div>
        </div>
        <div className="border border-border rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setIsHowItWorksExpanded(!isHowItWorksExpanded)}
            className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors focus:outline-none focus:ring-0"
          >
            <h4 className="text-sm font-semibold text-foreground">
              How does it work?
            </h4>
            <ChevronUp
              className={`w-4 h-4 text-muted-foreground transition-transform ${
                isHowItWorksExpanded ? '' : 'rotate-180'
              }`}
            />
          </button>

          {isHowItWorksExpanded && (
            <div className="px-4 pb-4 border-t border-border">
              <div className="mt-4 space-y-4">
                {/* Step 1: Select which profile to use as primary */}
                <div>
                  <h5 className="text-xs font-semibold text-foreground mb-2">
                    Step 1: Select which profile to use as primary
                  </h5>
                  <div className="grid grid-cols-2 gap-3">
                    {/* OLDEST Profile Card */}
                    <div
                      className={`p-3 border-2 rounded-xl transition-all ${
                        selectedMergeOption === 'oldest'
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-card'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ring-1 ring-border">
                          <span className="text-muted-foreground text-xs font-medium">
                            K
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-foreground">
                            Kate Smith
                          </div>
                          <div className="text-xs text-info">ID: 1234</div>
                          <div className="text-xs text-primary">
                            kate@yahoo.com
                          </div>
                          <div className="text-xs text-muted-foreground">
                            created: 3 weeks ago
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-border">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Oldest
                        </div>
                      </div>
                    </div>

                    {/* NEWEST Profile Card */}
                    <div
                      className={`p-3 border-2 rounded-xl transition-all ${
                        selectedMergeOption === 'newest'
                          ? 'border-primary bg-primary/10'
                          : 'border-border bg-card'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ring-1 ring-border">
                          <span className="text-muted-foreground text-xs font-medium">
                            K
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-foreground">
                            K Smith
                          </div>
                          <div className="text-xs text-info">ID: 5678</div>
                          <div className="text-xs text-primary">
                            kate@yahoo.com
                          </div>
                          <div className="text-xs text-muted-foreground">
                            created: 1 week ago
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-border">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Newest
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Merge Flow Indicator */}
                <div className="flex items-center justify-center py-1">
                  <div className="flex items-center gap-1.5 text-primary">
                    <div className="flex items-center gap-0.5">
                      <div className="w-6 h-0.5 bg-primary rounded-full" />
                      <div className="w-0 h-0 border-l-[6px] border-l-primary border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider">
                      Merges into
                    </span>
                    <div className="flex items-center gap-0.5">
                      <div className="w-0 h-0 border-r-[6px] border-r-primary border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent" />
                      <div className="w-6 h-0.5 bg-primary rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Step 2: Merged profile result */}
                <div>
                  <h5 className="text-xs font-semibold text-foreground mb-2">
                    Step 2: Merged profile result
                  </h5>
                  <div className="max-w-md mx-auto">
                    <div className="p-3 border-2 border-primary rounded-xl bg-primary/10">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-foreground text-xs font-medium">
                            K
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-foreground">
                            {selectedMergeOption === 'oldest'
                              ? 'Kate Smith'
                              : 'K Smith'}
                          </div>
                          <div className="text-xs text-info">
                            ID:{' '}
                            {selectedMergeOption === 'oldest' ? '1234' : '5678'}
                          </div>
                          <div className="text-xs text-primary">
                            kate@yahoo.com
                          </div>
                          <div className="text-xs text-muted-foreground">
                            created:{' '}
                            {selectedMergeOption === 'oldest'
                              ? '3 weeks ago'
                              : '1 week ago'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t border-primary/30">
                        <div className="text-xs font-semibold text-primary uppercase tracking-wider">
                          Merged Profile
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Warning Message */}
        <div className="flex items-start gap-3 p-4 bg-destructive-background border border-destructive/50 rounded-xl">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
          </div>
          <div>
            <p className="text-sm font-medium text-foreground">
              Merging contacts can't be undone
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Merged profiles could be added to new segments and trigger
              messages.{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  // Handle learn more action
                }}
                className="underline hover:text-destructive focus:outline-none focus:ring-0 text-destructive/90"
              >
                Learn more
              </button>
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            size="small"
            className="flex items-center gap-2"
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin shrink-0" />
            )}
            {isLoading ? 'Updating...' : 'Disable Duplicate Emails'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DisableDuplicateEmailModal;
